import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtDateTime, ROLE_LABELS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Search, RefreshCw } from "lucide-react";

const device = (ua = "") => {
  const os = /Windows/i.test(ua) ? "Windows" : /iPhone|iPad/i.test(ua) ? "iOS" : /Android/i.test(ua) ? "Android" : /Mac OS/i.test(ua) ? "macOS" : /Linux/i.test(ua) ? "Linux" : "";
  const br = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "";
  return [br, os].filter(Boolean).join(" · ") || "—";
};

export const AccessLogs = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [esito, setEsito] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const pageSize = 50;

  const load = useCallback(() => {
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (esito !== "all") params.esito = esito;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/access-logs", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); }).catch(() => {});
  }, [page, q, esito, from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, esito, from, to]);
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card className="p-5 space-y-4" data-testid="access-logs-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <div>
            <h2 className="font-heading font-semibold">Log Accessi</h2>
            <p className="text-xs text-muted-foreground">Accessi riusciti e tentativi falliti · conservati 12 mesi</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="access-logs-refresh"><RefreshCw className="h-4 w-4 mr-2" />Aggiorna</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca email, nome o IP…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="access-logs-search" />
        </div>
        <Select value={esito} onValueChange={setEsito}>
          <SelectTrigger className="w-40" data-testid="access-logs-esito"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli esiti</SelectItem>
            <SelectItem value="ok">Riusciti</SelectItem>
            <SelectItem value="ko">Falliti</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="access-logs-from" />
        <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="access-logs-to" />
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[760px] text-sm" data-testid="access-logs-table">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Data/Ora", "Utente", "Email", "Esito", "IP", "Dispositivo"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`access-log-row-${l.id}`}>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtDateTime(l.created_at)}</td>
                <td className="px-3 py-2">{l.user_name || "—"}{l.role && <span className="block text-xs text-muted-foreground">{ROLE_LABELS[l.role] || l.role}</span>}</td>
                <td className="px-3 py-2 break-all">{l.email}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {l.success
                    ? <span className="inline-flex rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 text-xs font-semibold">Riuscito</span>
                    : <span className="inline-flex rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 px-2 py-0.5 text-xs font-semibold" title={l.reason}>Fallito · {l.reason}</span>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{l.ip || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground" title={l.user_agent}>{device(l.user_agent)}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Nessun accesso registrato</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span data-testid="access-logs-total">{total} accessi</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="access-logs-prev">Prec</Button>
          <span>{page}/{pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="access-logs-next">Succ</Button>
        </div>
      </div>
    </Card>
  );
};
